import type { Message } from './types';

/** t291u: one match from searching inside a conversation (thread replies included). */
export interface ChatSearchHit {
  id: string;
  /** The thread root when the match is a reply; null for a top-level message. */
  parent_id: string | null;
  sequence: number;
  /** Sequence of the top-level message to scroll to: the message itself, or its thread root. */
  root_sequence: number;
  sender_id: string;
  created_at: string;
  /** Plain-text excerpt around the first match (or the matching file name). */
  snippet: string;
  /** Names of attachments that matched, if any. */
  files: string[];
}
export interface ChatSearchResult { hits: ChatSearchHit[]; total: number; truncated: boolean }
/** t291u: the first top-level message on or after a London date, or null when there is none. */
export interface ChatDateHit { message: { id: string; sequence: number; created_at: string } | null }

/**
 * Merge incoming messages into a list ordered by sequence, deduplicating by id.
 * Later copies (edits, deletes, reaction updates) replace earlier ones.
 * Realtime delivery is at-least-once, so clients must always merge through this.
 */
export function mergeMessages(existing: Message[], incoming: Message[]): Message[] {
  if (incoming.length === 0) return existing;
  const byId = new Map<string, Message>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) {
    const prev = byId.get(m.id);
    byId.set(m.id, prev ? { ...prev, ...m, reactions: m.reactions ?? prev.reactions, attachments: m.attachments ?? prev.attachments } : m);
  }
  return [...byId.values()].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Given the highest contiguous sequence a client holds and a newly received sequence,
 * decide whether a gap exists that must be filled via GET ...?after_seq=<highest>.
 */
export function detectGap(highestContiguous: number, received: number): boolean {
  return received > highestContiguous + 1;
}

/** Highest sequence N such that every sequence in (floor, N] is present. */
export function highestContiguousSequence(sequences: Iterable<number>, floor: number): number {
  const set = new Set(sequences);
  let n = floor;
  while (set.has(n + 1)) n++;
  return n;
}

/** Extract @mention user ids encoded as <@uuid> tokens in a message body. */
export function extractMentionIds(body: string): string[] {
  const ids = new Set<string>();
  for (const m of body.matchAll(/<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/gi)) ids.add(m[1].toLowerCase());
  return [...ids];
}

/** Extract task references such as WH-12 from a message body. */
export function extractTaskRefs(body: string): { key: string; number: number }[] {
  const out: { key: string; number: number }[] = [];
  const seen = new Set<string>();
  for (const m of body.matchAll(/\b([A-Z][A-Z0-9]{1,9})-(\d{1,7})\b/g)) {
    const k = `${m[1]}-${m[2]}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push({ key: m[1], number: Number(m[2]) });
    }
  }
  return out;
}

// ---------------------------------------------------------------- forwarding (t136)
/** metadata.forward on a forwarded message: a snapshot of the original, so it reads the same for people who can't open the source. */
export interface ForwardSnapshot {
  message_id: string; conversation_id: string;
  /** "#general", "a direct message", "a group chat", "a discussion". */
  from_label: string;
  sender_id: string | null; sender_name: string; sent_at: string;
  body: string; attachment_count: number;
  /** false: the forwarder added no comment and the message body is just the original text. */
  has_comment: boolean;
}
export const forwardOf = (m: { metadata?: Record<string, unknown> | null }) => (m.metadata?.forward ?? null) as ForwardSnapshot | null;

// ---------------------------------------------------------------- entity mentions (t121)
/** Things you can mention in chat with #. Wire format: <#kind:uuid> in the message body. */
export const MENTION_KINDS = ['building', 'task', 'project', 'journey', 'issue', 'channel'] as const;
export type MentionKind = typeof MENTION_KINDS[number];
export const MENTION_KIND_LABEL: Record<MentionKind, string> = { building: 'Buildings', task: 'Tasks', project: 'Projects', journey: 'Journeys', issue: 'Issues', channel: 'Channels' };
/** One mentionable item, as the viewer is allowed to see it. */
export interface MentionItem { kind: MentionKind; id: string; label: string; sub?: string | null }
export const ENTITY_TOKEN = /<#(building|task|project|journey|issue|channel):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/gi;
export const entityToken = (kind: MentionKind, id: string) => `<#${kind}:${id}>`;
/** `kind:id` refs in a body, deduplicated, lower-case ids. */
export function extractEntityRefs(body: string): string[] {
  const out = new Set<string>();
  for (const m of body.matchAll(ENTITY_TOKEN)) out.add(`${m[1].toLowerCase()}:${m[2].toLowerCase()}`);
  return [...out];
}
/** Permission-safe plain text for previews (notifications, email): never leaks the item's name. */
export const plainEntityTokens = (body: string) => body.replace(ENTITY_TOKEN, (_m, kind: string) => `#${kind.toLowerCase()}`);
